import {
  LEVELING_AGENT_TOOL_CALL_STATUSES,
  LEVELING_AGENT_TOOL_REGISTRY,
  LEVELING_AGENT_WRITE_TOOLS,
} from '../constants/levelingAgent';
import { getSubdomain } from './uiUtils';
import { API_DOMAIN, API_PROTOCOL } from './http';

export const prepareKadarApiUrl = () => {
  const subdomain = getSubdomain(window.location.hostname);

  const url =
    subdomain && subdomain.length > 0
      ? `${API_PROTOCOL}://${subdomain}.api.${API_DOMAIN}/`
      : `${API_PROTOCOL}://api.${API_DOMAIN}/`;

  return url.replace(/\/+$/, '');
};

export const prepareLevelingAgentConfig = ({ kadarApiToken } = {}) => ({
  kadar_api_url: prepareKadarApiUrl(),
  kadar_api_token: kadarApiToken,
});

export const messageContentToText = content => {
  if (typeof content === 'string') {
    return content;
  }

  if (Array.isArray(content)) {
    return content
      .map(item => {
        if (typeof item === 'string') {
          return item;
        }
        if (!item || typeof item !== 'object') {
          return '';
        }
        if (item.type === 'text' && typeof item.text === 'string') {
          return item.text;
        }
        if (typeof item.content === 'string') {
          return item.content;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }

  if (content && typeof content === 'object') {
    if (typeof content.text === 'string') {
      return content.text;
    }
    if (typeof content.content === 'string') {
      return content.content;
    }
  }

  return '';
};

export const messageContentToReasoning = content => {
  if (!Array.isArray(content)) {
    return [];
  }

  return content
    .filter(item => item?.type === 'reasoning' && item.reasoning)
    .map((item, index) => ({
      id: `reasoning-${index}`,
      reasoning: item.reasoning,
    }));
};

export const getToolCallsForMessage = (message, toolCalls = []) => {
  const messageToolCalls = message?.tool_calls ?? [];
  const ids = new Set(messageToolCalls.map(toolCall => toolCall.id));

  if (!ids.size) {
    return [];
  }

  return toolCalls.filter(toolCall => ids.has(toolCall.call?.id));
};

export const resolveToolCallStatus = ({ result, state }) => {
  if (state === LEVELING_AGENT_TOOL_CALL_STATUSES.PENDING) {
    return LEVELING_AGENT_TOOL_CALL_STATUSES.PENDING;
  }

  if (state === LEVELING_AGENT_TOOL_CALL_STATUSES.ERROR) {
    return LEVELING_AGENT_TOOL_CALL_STATUSES.ERROR;
  }

  const text = messageContentToText(result?.content);
  if (text) {
    try {
      const parsed = JSON.parse(text);
      if (parsed?.error) {
        return LEVELING_AGENT_TOOL_CALL_STATUSES.ERROR;
      }
    } catch {
      // Non-JSON tool output is treated as success.
    }
  }

  return LEVELING_AGENT_TOOL_CALL_STATUSES.COMPLETED;
};

export const parseCompletedWriteToolResult = ({ call, result, state }) => {
  if (!call.id || !LEVELING_AGENT_WRITE_TOOLS.includes(call.name)) {
    return null;
  }
  if (state !== LEVELING_AGENT_TOOL_CALL_STATUSES.COMPLETED) {
    return null;
  }

  const text = messageContentToText(result?.content);
  try {
    const parsed = JSON.parse(text);
    if (parsed?.error) {
      return null;
    }
    return { toolCallId: call.id, tool: call.name, data: parsed };
  } catch {
    return null;
  }
};

export const getLevelingAgentToolMeta = toolName =>
  LEVELING_AGENT_TOOL_REGISTRY[toolName] ?? {
    type: 'unknown',
    labelKey: null,
  };
